import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaLinkedinIn } from "react-icons/fa";
import { SiGooglescholar, SiResearchgate } from "react-icons/si";
import { StyledSection } from "../../components/common/sections";
import MyContainer from "../../components/ui/myContainer/MyContainer";
import {
  PageWrapper,
  ExpertsGrid,
  ExpertCard,
  FlipInner,
  FaceFront,
  FaceBack,
  AvatarRing,
  Avatar,
  ExpertName,
  ExpertRole,
  BackName,
  LinksList,
  LinkItem,
} from "./experts.styles";

import theodore from "../../assets/images/who-are-we/theodore.jpeg";
import ahmed from "../../assets/images/who-are-we/ahmed-shmarri.png";
import moaaz from "../../assets/images/who-are-we/moaaz.png";
import bandar from "../../assets/images/who-are-we/bandar.png";
import manal from "../../assets/images/who-are-we/manal.jpeg";
import entisar from "../../assets/images/who-are-we/entisar.png";
import seba from "../../assets/images/who-are-we/seba.jpg";
import abdaziz from "../../assets/images/who-are-we/abd-aziz.jpg";

const expertImages = [theodore, ahmed, moaaz, bandar, manal, entisar, seba, abdaziz];

const expertLinks = [
  {
    scholar: "https://scholar.google.com/citations?user=-b-tzzgAAAAJ&hl=en",
    researchGate: "https://www.researchgate.net/profile/Theodore-Papenfuss",
    linkedin:
      "https://www.linkedin.com/in/ted-papenfuss-33909a99",
  },
  {
    scholar: "https://scholar.google.com/citations?user=7yGB5xcAAAAJ&hl=ar",
    researchGate: "https://www.researchgate.net/profile/Ahmed-Alshammari-2",
    linkedin: "https://www.linkedin.com/in/prof-ahmed-alferaisy-b83031244",
  },
  {
    scholar: "https://scholar.google.com/citations?user=G7Y9KvkAAAAJ&hl=fr",
    researchGate: "https://www.researchgate.net/profile/Mouad-Mkamel-2",
    linkedin: "https://www.linkedin.com/in/mouad-mkamel-67264529b",
  },
  {
    scholar: "https://scholar.google.com/citations?user=puLusQYAAAAJ&hl=en",
    researchGate: "https://www.researchgate.net/profile/Bandar-Aloufi",
    linkedin: "https://www.linkedin.com/in/bandar-aloufi-381880333",
  },
  {
    scholar: "https://scholar.google.com/citations?user=X2wE-EgAAAAJ&hl=en",
    researchGate: "https://www.researchgate.net/profile/Manal-Siyam",
    linkedin: "https://de.linkedin.com/in/dr-manal-siyam-9167963b",
  },
  {
    scholar: "https://scholar.google.com/citations?user=FXJaE00AAAAJ&hl=ar",
    researchGate: "https://www.researchgate.net/profile/Intesar-Salih",
    linkedin: "https://www.linkedin.com/in/intesar-salih-1b20b7137",
  },
  {
    scholar:
      "https://scholar.google.com/citations?hl=en&user=E3anCrcAAAAJ&view_op=list_works&authuser=1",
    researchGate: null,
    linkedin: "https://www.linkedin.com/in/seba-k-alobathani-1056bb1a4",
  },
  {
    scholar: "https://scholar.google.com/citations?user=itJC9NQAAAAJ&hl=en",
    researchGate:
      "https://www.researchgate.net/profile/Abdulaziz-Arif-A-Alshammari-2",
    linkedin: "https://www.linkedin.com/in/abdulaziz-arif-alshammari-28b042193",
  },
];

const PLATFORMS = [
  {
    key: "scholar",
    label: "Google Scholar",
    icon: SiGooglescholar,
    color: "#4285F4",
  },
  {
    key: "researchGate",
    label: "ResearchGate",
    icon: SiResearchgate,
    color: "#00CCBB",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: FaLinkedinIn,
    color: "#0A66C2",
  },
];

const RevealCard = ({ delay, member, image, links }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const availablePlatforms = PLATFORMS.filter((p) => links && links[p.key]);

  return (
    <ExpertCard
      ref={ref}
      $delay={delay}
      className={`${visible ? "is-visible" : ""} ${flipped ? "is-flipped" : ""}`}
      onClick={() => setFlipped((f) => !f)}
    >
      <FlipInner>
        <FaceFront>
          <AvatarRing>
            <Avatar src={image} alt={member.name} />
          </AvatarRing>
          <ExpertName>{member.name}</ExpertName>
          <ExpertRole>{member.role}</ExpertRole>
        </FaceFront>

        <FaceBack>
          <BackName>{member.name}</BackName>
          <LinksList>
            {availablePlatforms.map((platform, i) => {
              const Icon = platform.icon;
              return (
                <LinkItem
                  key={platform.key}
                  href={links[platform.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  $color={platform.color}
                  $delay={150 + i * 110}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Icon />
                  <span>{platform.label}</span>
                </LinkItem>
              );
            })}
          </LinksList>
        </FaceBack>
      </FlipInner>
    </ExpertCard>
  );
};

const Experts = () => {
  const { t } = useTranslation("experts");
  const members = t("members", { returnObjects: true }) || [];

  return (
    <PageWrapper>
      <MyContainer>
        <StyledSection>
          <ExpertsGrid>
            {Array.isArray(members) &&
              members.map((member, idx) => (
                <RevealCard
                  key={idx}
                  delay={idx * 90}
                  member={member}
                  image={expertImages[idx]}
                  links={expertLinks[idx]}
                />
              ))}
          </ExpertsGrid>
        </StyledSection>
      </MyContainer>
    </PageWrapper>
  );
};

export default Experts;
